import { notEmpty } from '@festina-life/core'

import type { EventVersionWithCommandType } from '../hooks/useFilterCommands'

export const getEvents = (events: EventVersionWithCommandType[]) =>
  events.filter(
    (event) =>
      event.rolledBackTime === undefined &&
      !['CloseCommitmentStep', 'HandleDeathCommutationStep'].includes(
        event.commandStepTemplateId ?? '',
      ),
  )

export const getLatestEvent = (events: EventVersionWithCommandType[]) => {
  const eventsWithoutRolledbackEvents = getEvents(events)

  return eventsWithoutRolledbackEvents.at(0)
}

/**
 * Builds a Set of processIds
 * Use this with hasManuallyBeenCancelled for O(1) lookups instead of O(n) scans.
 * Should be memoized at the component level to avoid rebuilding on every render.
 *
 * @param allEvents - All events to extract processIds from
 * @returns Set of processIds
 */
export const buildProcessIdSet = (allEvents: EventVersionWithCommandType[]) => {
  const processIds = new Set<string>()
  for (const { processId } of allEvents) {
    if (notEmpty(processId)) {
      processIds.add(processId)
    }
  }
  return processIds
}

/**
 * Determines if an event has been manually cancelled.
 * An event is considered manually cancelled when:
 * 1. It has a rolledBackProcessId (indicating it was rolled back)
 * 2. Its rolledBackProcessId is NOT present in processIds
 *
 * @param eventVersion - The event to check
 * @param processIds - Precomputed Set of processIds for O(1) lookups
 * @returns true if the event was manually cancelled, false otherwise
 */
export const hasManuallyBeenCancelled = ({
  eventVersion,
  processIds,
}: {
  eventVersion: EventVersionWithCommandType
  processIds: Set<string>
}): boolean => {
  if (!eventVersion?.rolledBackProcessId) {
    return false
  }

  const rollbackEventExists = processIds.has(eventVersion.rolledBackProcessId)

  return !rollbackEventExists
}
